'use client'

import { useEffect, useMemo, useState } from 'react'
import i18next, { type InitOptions } from 'i18next'
import LanguageDetector from 'i18next-browser-languagedetector'

import languages from '../assets/locales/languages.json'
import resources from '../assets/locales/locales.json'
import translationKeys from '../assets/locales/translationKeys.json'

export const defaultLanguage = 'en'

export const i18nOptions: InitOptions = {
  fallbackLng: defaultLanguage,
  resources,
  ns: ['translations'],
  defaultNS: 'translations',
  nsSeparator: false,
  returnObjects: true,
  debug: false,
  interpolation: {
    escapeValue: false
  },
  detection: {
    order: [
      'cookie',
      'localStorage',
      'sessionStorage',
      'navigator',
      'htmlTag',
      'path',
      'subdomain'
    ],
    lookupCookie: 'lng',
    lookupLocalStorage: 'lng',
    lookupFromPathIndex: 0,
    lookupFromSubdomainIndex: 0,
    caches: ['localStorage', 'cookie'],
    excludeCacheFor: ['cimode'],
    cookieOptions: { path: '/', sameSite: 'strict' }
  }
}

export const changeLanguage: (l: string) => void = language =>
  i18next.changeLanguage(language)

export const t: TFunction = key => {
  if (process.env.NEXT_PUBLIC_GET_TRANSLATION_KEYS === 'true') {
    if (
      !translationKeys.includes(key) &&
      !key.includes('++dnt++') &&
      /(?=.*[a-zA-Z0-9])/.test(key)
    ) {
      fetch('/api/i18n', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(key)
      })
      return key
    }
    return key?.replace?.('++dnt++', '')
  }
  // return i18next.t(key, interpolation ?? {})?.replace?.('++dnt++', '')
  return key
}

export type TFunction = (
  key: string,
  interpolation?: Record<string, string>
) => string

export const I18nProvider: React.FC<{
  children?: React.ReactNode
  lang: string
}> = ({ children, lang }) => {
  const [i18nInitiated, setI18nInitiated] = useState(false)
  const language = useMemo(
    () =>
      languages
        .find(l => l.language.toLowerCase() === lang?.slice(0, 2))
        ?.language?.toLowerCase() ?? 'en',
    [lang]
  )

  if (i18nInitiated && lang != null && typeof lang === 'string') {
    changeLanguage(language)
  }

  useEffect(() => {
    i18next.use(LanguageDetector).init(i18nOptions).catch(console.warn)
    setI18nInitiated(true)
  }, [])

  if (!i18nInitiated) return null
  return <>{children}</>
}
